import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import useAxios from '@/services/api/useAxios'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import { afterReceivingPayload, afterReceivingSession } from './hooks'
import type { TPlanDraft, TPlanDraftAssistantAnswer, TPlanDraftMainSwitch, TPlanDraftTrainerPreview, TPlanDraftYearSession, TPlanDraftNewSession, TPlanDraftOperation, TPlanDraftPayload, TPlanDraftReferences, TPlanDraftTemplate } from './trainingPlanDraftInterface'
import type { AssistantMention } from '@/components/application/searchCommand/chat/assistantMention/types'

const resource = 'trainingPlanDrafts'
const base = `${BASE_URL}/${API_MAIN_VERSION}/${resource}`

const show = (axios: IFactoryFunctionRequest<TPlanDraftPayload, undefined>) => (year: number, draftId?: string | null) => axios({
  method: EMethod.get,
  url: base,
  query: { year: String(year), ...(draftId && { draftId }) },
  afterReceivingHook: afterReceivingPayload,
})

const rename = (axios: IFactoryFunctionRequest<{ draft: TPlanDraft }, any>) => (draftId: string, name: string) => axios({
  method: EMethod.patch,
  url: `${base}/${draftId}`,
  data: { name },
})

const duplicate = (axios: IFactoryFunctionRequest<{ draft: TPlanDraft }, any>) => (draftId: string, name: string) => axios({
  method: EMethod.post,
  url: `${base}/${draftId}/duplicate`,
  data: { name },
})

const mainPreview = (axios: IFactoryFunctionRequest<{ summary: TPlanDraftMainSwitch, currentName: string | null, currentSentAt: string | null }, undefined>) => (draftId: string) => axios({
  method: EMethod.get,
  url: `${base}/${draftId}/main`,
})

const setMain = (axios: IFactoryFunctionRequest<{ draft: TPlanDraft, summary: TPlanDraftMainSwitch }, any>) => (draftId: string, resend: boolean) => axios({
  method: EMethod.post,
  url: `${base}/${draftId}/main`,
  data: { resend },
})

const revoke = (axios: IFactoryFunctionRequest<{ revoked: number, keptValidated: number }, any>) => (draftId: string, sessionIds: string[]) => axios({
  method: EMethod.post,
  url: `${base}/${draftId}/revoke`,
  data: { sessionIds },
})

const trainerPreview = (axios: IFactoryFunctionRequest<{ trainers: TPlanDraftTrainerPreview[] }, any>) => (draftId: string, data: { trainerIds: string[], sessionId: string, days: string[] }) => axios({
  method: EMethod.post,
  url: `${base}/${draftId}/trainer-preview`,
  data,
})

const yearSessions = (axios: IFactoryFunctionRequest<{ year: number, sessions: TPlanDraftYearSession[] }, undefined>) => (year: number) => axios({
  method: EMethod.get,
  url: `${base}/years/${year}/sessions`,
  afterReceivingHook: (data: any) => ({ ...data, sessions: (data?.sessions || []).map(afterReceivingSession) }),
})

const exportExcel = (axios: IFactoryFunctionRequest<{ base64: string, fileName: string }, undefined>) => (draftId: string) => axios({
  method: EMethod.get,
  url: `${base}/${draftId}/export`,
})

const generate = (axios: IFactoryFunctionRequest<{ draft: TPlanDraft }, any>) => (data: { year: number, sourceYear?: number, shiftMode?: 'weekday' | 'date', blank?: boolean, name?: string }) => axios({
  method: EMethod.post,
  url: `${base}/generate`,
  data,
})

const remove = (axios: IFactoryFunctionRequest<{ ok: boolean }, undefined>) => (draftId: string) => axios({
  method: EMethod.delete,
  url: `${base}/${draftId}`,
})

const updateSession = (axios: IFactoryFunctionRequest<{ ok: boolean }, any>) => (draftId: string, sessionId: string, data: {
  startDay?: string
  slots?: { start: string, end: string, room: string | null, location: string }[]
  internalTrainer?: string[]
}) => axios({
  method: EMethod.patch,
  url: `${base}/${draftId}/sessions/${sessionId}`,
  data,
})

const addSession = (axios: IFactoryFunctionRequest<{ sessionId: string }, TPlanDraftNewSession>) => (draftId: string, data: TPlanDraftNewSession) => axios({
  method: EMethod.post,
  url: `${base}/${draftId}/sessions`,
  data,
})

const template = (axios: IFactoryFunctionRequest<TPlanDraftTemplate, undefined>) => (trainingId: string) => axios({
  method: EMethod.get,
  url: `${base}/templates/${trainingId}`,
})

const references = (axios: IFactoryFunctionRequest<TPlanDraftReferences, undefined>) => (search: string) => axios({
  method: EMethod.get,
  url: `${base}/references`,
  query: { search },
})

const removeSession = (axios: IFactoryFunctionRequest<{ ok: boolean }, undefined>) => (draftId: string, sessionId: string) => axios({
  method: EMethod.delete,
  url: `${base}/${draftId}/sessions/${sessionId}`,
})

const removeTrainingSessions = (axios: IFactoryFunctionRequest<{ deleted: number, keptLocked: number, keptCreated: number }, undefined>) => (draftId: string, trainingId: string) => axios({
  method: EMethod.delete,
  url: `${base}/${draftId}/trainings/${trainingId}`,
})

const setValidation = (axios: IFactoryFunctionRequest<{ updated: number }, any>) => (draftId: string, data: { sessionIds: string[], action: 'validate' | 'unvalidate', resourceId?: string }) => axios({
  method: EMethod.post,
  url: `${base}/${draftId}/validation`,
  data,
})

const send = (axios: IFactoryFunctionRequest<{ sessions: number, trainers: number, notified: number, skipped: number }, any>) => (draftId: string, sessionIds: string[], notify = true) => axios({
  method: EMethod.post,
  url: `${base}/${draftId}/send`,
  data: { sessionIds, notify },
})

const createSessions = (axios: IFactoryFunctionRequest<{ created: { sessionId: string, trainingSessionId: string }[], failed: { sessionId: string, code: string, message?: string }[] }, any>) => (draftId: string, sessionIds: string[]) => axios({
  method: EMethod.post,
  url: `${base}/${draftId}/create`,
  data: { sessionIds },
})

const assistant = (axios: IFactoryFunctionRequest<TPlanDraftAssistantAnswer, any>) => (draftId: string, data: {
  message: string
  mentions: AssistantMention[]
  attachments: { name: string, mimeType: string, dataBase64: string }[]
  history: { role: 'user' | 'assistant', content: string, mentions?: AssistantMention[] }[]
  pendingOperations: TPlanDraftOperation[]
  language: string
}) => axios({
  method: EMethod.post,
  url: `${base}/${draftId}/assistant`,
  data,
})

const applyOperations = (axios: IFactoryFunctionRequest<{ applied: number, skipped: { sessionId: string, code: string }[] }, any>) => (draftId: string, operations: TPlanDraftOperation[]) => axios({
  method: EMethod.post,
  url: `${base}/${draftId}/operations`,
  data: { operations },
})

const mine = (axios: IFactoryFunctionRequest<TPlanDraftPayload, undefined>) => () => axios({
  method: EMethod.get,
  url: `${base}/mine`,
  afterReceivingHook: afterReceivingPayload,
})

const respond = (axios: IFactoryFunctionRequest<{ updated: number }, any>) => (data: { sessionIds: string[], status: 'validated' | 'declined' | 'pending', comment?: string }) => axios({
  method: EMethod.post,
  url: `${base}/mine/respond`,
  data,
})

export default (axios: AxiosInstance) => ({
  show: show(useAxios(axios)),
  rename: rename(useAxios(axios)),
  duplicate: duplicate(useAxios(axios)),
  mainPreview: mainPreview(useAxios(axios)),
  setMain: setMain(useAxios(axios)),
  revoke: revoke(useAxios(axios)),
  exportExcel: exportExcel(useAxios(axios)),
  yearSessions: yearSessions(useAxios(axios)),
  trainerPreview: trainerPreview(useAxios(axios)),
  generate: generate(useAxios(axios)),
  remove: remove(useAxios(axios)),
  updateSession: updateSession(useAxios(axios)),
  addSession: addSession(useAxios(axios)),
  template: template(useAxios(axios)),
  references: references(useAxios(axios)),
  removeSession: removeSession(useAxios(axios)),
  removeTrainingSessions: removeTrainingSessions(useAxios(axios)),
  setValidation: setValidation(useAxios(axios)),
  send: send(useAxios(axios)),
  createSessions: createSessions(useAxios(axios)),
  assistant: assistant(useAxios(axios)),
  applyOperations: applyOperations(useAxios(axios)),
  mine: mine(useAxios(axios)),
  respond: respond(useAxios(axios)),
})
